import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const stripePromise = loadStripe(import.meta.env.VITE_PUBLIC_STRIPE_PUBLISHABLE_KEY || '');

interface PlanDetails {
  name: string;
  price: number;
  priceId: string;
  features: string[];
}

const plans: Record<string, PlanDetails> = {
  basic: {
    name: 'Basic',
    price: 29,
    priceId: 'price_basic_monthly',
    features: [
      'Up to 50 members',
      'Meeting scheduling',
      'Dues payment processing',
      'Email support'
    ]
  },
  pro: {
    name: 'Pro',
    price: 79,
    priceId: 'price_pro_monthly',
    features: [
      'Up to 200 members',
      'HD video meetings',
      'Automated billing',
      'Document storage',
      'Priority support'
    ]
  },
  enterprise: {
    name: 'Enterprise',
    price: 199,
    priceId: 'price_enterprise_monthly',
    features: [
      'Unlimited members',
      'Custom branding',
      'Advanced analytics',
      'API access',
      'Dedicated support'
    ]
  }
};

function PaymentForm({ clientSecret, onSuccess }: { clientSecret: string; onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/subscribe?success=true`,
      },
    });

    if (error) {
      setErrorMessage(error.message || 'Payment failed');
      setIsProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-teal-500 text-white py-4 rounded-xl font-medium hover:bg-teal-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        {isProcessing ? 'Processing...' : 'Complete Subscription'}
      </button>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {errorMessage}
        </div>
      )}
    </form>
  );
}

export default function SubscribePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string>('pro');
  const [showPayment, setShowPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [communityName, setCommunityName] = useState('');
  const [email, setEmail] = useState('');
  const [memberCount, setMemberCount] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    checkUser();
    
    // Check for success parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 4000);
    }
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      setUser(user);
      setEmail(user.email || '');
    }
    setLoading(false);
  };

  const handleSignIn = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/subscribe`,
      },
    });
  };

  const handleSubscribe = async () => {
    if (!communityName || !email || !memberCount) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const plan = plans[selectedPlan];

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-platform-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            email,
            communityName,
            memberCount: parseInt(memberCount),
            planName: plan.name,
            planPrice: plan.price,
            priceId: plan.priceId,
          }),
        }
      );

      const data = await response.json();

      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setShowPayment(true);
      } else {
        throw new Error(data.error || 'Failed to create subscription');
      }
    } catch (error: any) {
      alert(error.message || 'Failed to set up subscription');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-sky-50 font-['Inter',sans-serif]">
        <div className="max-w-md mx-auto px-6 py-20">
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-community-line text-white text-3xl"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscribe to NeighborHub</h1>
              <p className="text-gray-600">Sign in to get started with your community</p>
            </div>

            <button
              onClick={handleSignIn}
              className="w-full bg-teal-500 text-white py-4 rounded-xl font-medium hover:bg-teal-600 transition-colors flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-google-line text-xl"></i>
              Sign In with Google
            </button>

            <div className="mt-6 text-center">
              <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-sky-50 flex items-center justify-center font-['Inter',sans-serif]">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-4xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Welcome to NeighborHub! 🎉</h1>
            <p className="text-lg text-gray-600 mb-6">
              Your subscription is now active! You have full access to all features.
            </p>
            <div className="bg-teal-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-teal-800">
                <strong>What's Next:</strong><br />
                • Access your dashboard<br />
                • Invite community members<br />
                • Schedule your first meeting<br />
                • Set up payment collection
              </p>
            </div>
            <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-['Inter',sans-serif]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Subscribe to NeighborHub</h1>
          <p className="text-xl text-gray-600">Choose your plan and start managing your community today</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Plan Selection */}
          <div className="lg:col-span-2">
            {!showPayment ? (
              <div className="bg-white rounded-3xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Your Plan</h2>

                {/* Plan Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {Object.entries(plans).map(([key, plan]) => (
                    <div
                      key={key}
                      onClick={() => setSelectedPlan(key)}
                      className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                        selectedPlan === key
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-teal-300'
                      }`}
                    >
                      <div className="text-center mb-4">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <div className="text-3xl font-bold text-teal-600">${plan.price}</div>
                        <div className="text-sm text-gray-600">/month</div>
                      </div>
                      <ul className="space-y-2">
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                            <i className="ri-check-line text-teal-600"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Community Details Form */}
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">Community Details</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Community Name *
                    </label>
                    <input
                      type="text"
                      value={communityName}
                      onChange={(e) => setCommunityName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      placeholder="Sunset Hills HOA"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      placeholder="admin@community.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Members *
                    </label>
                    <input
                      type="number"
                      value={memberCount}
                      onChange={(e) => setMemberCount(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      placeholder="50"
                      min="1"
                      required
                    />
                  </div>

                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                      <div className="text-sm text-teal-800">
                        <strong>Fully Automated:</strong> After payment, you'll get instant access. No waiting, no manual approval needed!
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="w-full bg-teal-500 text-white py-4 rounded-xl font-medium hover:bg-teal-600 transition-colors disabled:bg-gray-400 whitespace-nowrap cursor-pointer"
                  >
                    {loading ? 'Processing...' : 'Continue to Payment'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Complete Your Subscription</h2>
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-flashlight-line text-sky-600 text-xl mt-0.5"></i>
                    <div className="text-sm text-sky-800">
                      <strong>Instant Activation:</strong> Your account will be activated immediately after payment. You'll get full access right away!
                    </div>
                  </div>
                </div>
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <PaymentForm 
                    clientSecret={clientSecret} 
                    onSuccess={() => {
                      setShowSuccess(true);
                    }} 
                  />
                </Elements>
              </div>
            )}
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Plan</span>
                  <span className="font-semibold text-gray-900">{plans[selectedPlan].name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Billing</span>
                  <span className="font-semibold text-gray-900">Monthly</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-teal-600">${plans[selectedPlan].price}/mo</span>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">What's Included:</h4>
                <ul className="space-y-2">
                  {plans[selectedPlan].features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                      <i className="ri-check-line text-teal-600"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl shadow-lg p-8 text-white">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Secure Payment</h3>
              <p className="text-teal-100 text-sm leading-relaxed">
                Your payment information is encrypted and secure. Cancel anytime with no hidden fees.
              </p>
            </div>

            <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-3xl shadow-lg p-8 text-white">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4">
                <i className="ri-rocket-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">100% Automated</h3>
              <p className="text-sky-100 text-sm leading-relaxed">
                Everything happens automatically - instant access, payment tracking, and member management. Zero manual work required!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
